import type {
  StrategyUsageRecord,
  StrategyUsageSummaryResponse,
} from "@/types/api";
import { request } from "./transport";

export interface StrategyUsageRecordInput {
  strategy_key: string;
  occurred_on?: string;
  quantity?: number;
  pnl_amount?: number | null;
  return_basis_amount?: number | null;
  reported_return_rate?: number | null;
  return_rate?: number | null;
  currency?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface PositionCloseInput {
  opened_on?: string | null;
  occurred_on?: string;
  notes?: string;
  pnl_amount?: number;
  request_id: string;
  strategy_version: string;
  fees: number;
  fills: {
    leg_id: string;
    quantity: number;
    entry_price?: number | null;
    exit_price: number | null;
  }[];
}

export interface PositionCloseUpdateInput {
  opened_on?: string | null;
  occurred_on?: string;
  notes?: string;
  pnl_amount?: number;
  record_version: string;
  fees: number;
  fills: {
    leg_id: string;
    entry_price: number | null;
    exit_price: number | null;
  }[];
}

export interface StrategyUsageFilter {
  source?: string;
  strategy_key?: string;
  from?: string;
  to?: string;
}

function queryString(filter?: StrategyUsageFilter, page?: number) {
  const query = new URLSearchParams();
  if (page) query.set("page", String(page));
  if (filter?.source) query.set("source", filter.source);
  if (filter?.strategy_key) query.set("strategy_key", filter.strategy_key);
  if (filter?.from) query.set("from", filter.from);
  if (filter?.to) query.set("to", filter.to);
  const value = query.toString();
  return value ? `?${value}` : "";
}

export const strategyUsageRecords = {
  updateClose: (id: string, input: PositionCloseUpdateInput) =>
    request<{ strategy_usage_record: StrategyUsageRecord }>(
      `/strategy_usage_records/${id}/close`,
      { method: "PATCH", body: JSON.stringify({ closure: input }) },
    ),
  closePositions: (
    workspaceId: string,
    inputs: (PositionCloseInput & { strategy_id: string })[],
  ) =>
    request<{ strategy_usage_records: StrategyUsageRecord[] }>(
      `/ticker_workspaces/${workspaceId}/strategies/close_all`,
      { method: "POST", body: JSON.stringify({ closures: inputs }) },
    ),
  closePosition: (
    workspaceId: string,
    strategyId: string,
    input: PositionCloseInput,
  ) =>
    request<{ strategy_usage_record: StrategyUsageRecord }>(
      `/ticker_workspaces/${workspaceId}/strategies/${strategyId}/close`,
      { method: "POST", body: JSON.stringify({ closure: input }) },
    ),
  listStrategyUsageRecords: (filter?: StrategyUsageFilter, page?: number) =>
    request<{
      strategy_usage_records: StrategyUsageRecord[];
      next_page: number | null;
    }>(`/strategy_usage_records${queryString(filter, page)}`),
  getStrategyUsageSummary: (filter?: StrategyUsageFilter) =>
    request<StrategyUsageSummaryResponse>(
      `/strategy_usage_records/summary${queryString(filter)}`,
    ),
  createStrategyUsageRecord: (input: StrategyUsageRecordInput) =>
    request<{ strategy_usage_record: StrategyUsageRecord }>(
      "/strategy_usage_records",
      {
        method: "POST",
        body: JSON.stringify({ strategy_usage_record: input }),
      },
    ),
  updateStrategyUsageRecord: (id: string, input: StrategyUsageRecordInput) =>
    request<{ strategy_usage_record: StrategyUsageRecord }>(
      `/strategy_usage_records/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify({ strategy_usage_record: input }),
      },
    ),
  deleteStrategyUsageRecord: (id: string) =>
    request<{ ok: true }>(`/strategy_usage_records/${id}`, {
      method: "DELETE",
    }),
};
